/**
 * TabBar — tab navigation for the form panel.
 *
 * "Datos básicos" with a red "*" is always the first tab (key: 'basicData').
 * Additional tabs come from `definition.tabs`.
 * Error count badges are shown per tab from `errors` mapped to tab keys.
 * When navigationType='modal' + more than 1 content tab, logs console.warn and truncates.
 *
 * @module components/form/TabBar
 * @see FMPV-MVWC-E003-S001 I-004
 */
import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import type { MasterPatternViewDefinition } from '../../types/definition.types';
import { getAllFields } from '../../engine/fieldEngine';
import type { MasterDetailTabConfig } from '../../types/detail.types';
import type { MasterPatternViewEntity } from '../../types/service.types';

export interface TabBarProps {
  definition: MasterPatternViewDefinition;
  activeTabKey: string;
  errors: Record<string, string>;
  onTabChange: (key: string) => void;
  navigationType?: 'sidebar' | 'modal' | 'page' | 'inline';
  /** Form mode — detail tabs only shown in 'editing' mode */
  formMode?: 'creating' | 'editing' | 'viewing' | 'closed';
  /** Structured detail tab configurations (only visible in editing mode) */
  detailTabConfigs?: MasterDetailTabConfig<MasterPatternViewEntity>[];
  /** Translation function override — falls back to the current Spanish text when omitted. */
  t?: (key: string, defaultValue?: string) => string;
}

interface ResolvedTab {
  id: string;
  label: string;
  badge?: number;
}

/** Resolve the tab key for a field: uses formTab if set, otherwise 'basicData'. */
function getFieldTabKey(fieldName: string, formTab?: string): string {
  return formTab ?? 'basicData';
}

export function TabBar({
  definition,
  activeTabKey,
  errors,
  onTabChange,
  navigationType,
  formMode,
  detailTabConfigs,
  t,
}: TabBarProps) {
  const tt = useMemo(
    () => t ?? ((key: string, defaultValue?: string) => defaultValue ?? key),
    [t],
  );
  const items = useMemo((): ResolvedTab[] => {
    const contentTabs = definition.tabs ?? [];
    const isModal = navigationType === 'modal';
    const showDetailTabs = (formMode === 'creating' || formMode === 'editing' || formMode === 'viewing') && (detailTabConfigs?.length ?? 0) > 0;

    let visibleContentTabs = contentTabs;
    if (isModal && contentTabs.length > 1) {
      console.warn(
        '[MasterPatternView] navigationType="modal" with more than 1 content tab is not supported. ' +
          'Only "Datos básicos *" and the first content tab will be shown.'
      );
      visibleContentTabs = contentTabs.slice(0, 1);
    }

    // Count errors per tab key
    const errorCountByTab: Record<string, number> = {};
    for (const [fieldName] of Object.entries(errors)) {
      const field = getAllFields(definition).find((f) => f.fieldName === fieldName);
      const tabKey = getFieldTabKey(fieldName, field?.formTab);
      errorCountByTab[tabKey] = (errorCountByTab[tabKey] ?? 0) + 1;
    }

    const basicDataTab: ResolvedTab = {
      id: 'basicData',
      label: tt('basicDataTabLabel', 'Datos básicos'),
      badge: errorCountByTab['basicData'],
    };

    const additionalTabs: ResolvedTab[] = visibleContentTabs
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((t) => ({
        id: t.key,
        label: t.label,
        badge: errorCountByTab[t.key],
      }));

    const detailTabs: ResolvedTab[] = showDetailTabs
      ? (detailTabConfigs ?? []).map((dt) => ({ id: dt.key, label: dt.label }))
      : [];

    return [basicDataTab, ...additionalTabs, ...detailTabs];
  }, [definition, errors, navigationType, formMode, detailTabConfigs, tt]);

  // A single tab (just "Datos básicos", no content/detail tabs) has nothing to switch
  // between — showing a one-item tab strip only adds visual noise.
  if (items.length <= 1) return null;

  return (
    <div
      data-testid="tabs"
      // overflow-x-auto by itself makes the browser also treat overflow-y as
      // "auto" (per the CSS overflow spec — setting one axis forces the other
      // off "visible"), so a 1px content/box-sizing mismatch was enough to pop
      // a vertical scrollbar (complete with ugly up/down arrow buttons on
      // Windows) that had nothing real to scroll. overflow-y-hidden pins it.
      className="flex flex-shrink-0 overflow-x-auto overflow-y-hidden gap-1 px-3 pt-2"
    >
      {items.map((item) => {
        const isActive = item.id === activeTabKey;
        const isBasicData = item.id === 'basicData';

        return (
          <motion.button
            key={item.id}
            type="button"
            data-testid={`tab-${item.id}`}
            data-active={isActive}
            onClick={() => onTabChange(item.id)}
            whileTap={{ scaleX: 0.94 }}
            animate={{ scaleX: isActive ? 1.04 : 1 }}
            transition={{ type: 'spring', bounce: 0.55, duration: 0.5 }}
            className={`relative flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold whitespace-nowrap cursor-pointer rounded-t-lg bg-[#f8f9fc] dark:bg-zinc-900 transition-colors ${
              isActive
                ? 'text-blue-600 dark:text-blue-400'
                : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800'
            }`}
          >
            {isBasicData ? (
              <>
                {tt('basicDataTabLabel', 'Datos básicos')}{' '}
                <span className="text-red-500 text-xs">*</span>
              </>
            ) : (
              item.label
            )}
            {item.badge != null && item.badge > 0 && (
              <span
                data-testid={`tab-error-badge-${item.id}`}
                className="inline-flex items-center justify-center w-4 h-4 bg-[#dc2626] text-white rounded-full text-[9px] font-bold"
              >
                {item.badge}
              </span>
            )}
            {isActive && (
              <motion.div
                layoutId="mpv-tab-active-indicator"
                className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-blue-600 dark:bg-blue-400"
                transition={{ type: 'spring', bounce: 0.55, duration: 0.5 }}
              />
            )}
          </motion.button>
        );
      })}
    </div>
  );
}
